import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import axios from 'axios';
import { ArrowLeft, MapPin, Clock, Mail, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/fe0mwn3b_image.png";

export default function CareersPage() {
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/careers`).then(r => setVacancies(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-[#FDFCFB] border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="S.A.I." className="h-11" />
            <div><span className="text-base font-bold tracking-tight">S.A.I.</span>
              <span className="text-[10px] tracking-[0.2em] uppercase block text-[#5C5C5C] -mt-0.5">Design Group</span></div>
          </a>
          <Button variant="outline" onClick={() => navigate('/')} className="rounded-none border-[#121212] text-xs uppercase tracking-wider h-9">
            <ArrowLeft size={14} className="mr-2" /> Back to Home
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 px-6 border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-4 font-bold">Join Our Team</p>
          <h1 className="text-4xl md:text-5xl tracking-tighter leading-none font-bold text-[#121212] mb-4" data-testid="careers-title">
            Careers at S.A.I.
          </h1>
          <p className="text-base text-[#5C5C5C] max-w-xl leading-relaxed">
            We're always looking for talented architects, designers, and engineers who share our passion for creating extraordinary spaces.
          </p>
          <div className="flex items-center gap-2 mt-6">
            <Mail size={16} className="text-[#5C5C5C]" />
            <span className="text-sm text-[#2A2A2A]">Send your resume to <strong>saidesigngroup2013@gmail.com</strong></span>
          </div>
        </div>
      </section>

      {/* Vacancies */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {vacancies.length === 0 ? (
            <div className="border border-[#E5E5E5] bg-white p-16 text-center">
              <Briefcase size={40} className="mx-auto text-[#E5E5E5] mb-4" strokeWidth={1} />
              <p className="text-sm text-[#5C5C5C]">No open positions at the moment.</p>
              <p className="text-xs text-[#5C5C5C] mt-2">Send your resume to <strong>saidesigngroup2013@gmail.com</strong> for future opportunities.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {vacancies.map(v => (
                <div key={v.vacancy_id} className="border border-[#E5E5E5] bg-white" data-testid={`vacancy-${v.vacancy_id}`}>
                  <div className="p-6 cursor-pointer hover:bg-[#F5F5F5] transition-colors"
                    onClick={() => setSelected(selected === v.vacancy_id ? null : v.vacancy_id)}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-[#121212]">{v.title}</h3>
                        <div className="flex items-center gap-4 mt-2">
                          {v.department && <Badge variant="outline" className="rounded-none text-[10px] uppercase">{v.department}</Badge>}
                          {v.location && <span className="flex items-center gap-1 text-xs text-[#5C5C5C]"><MapPin size={12} /> {v.location}</span>}
                          {v.type && <span className="flex items-center gap-1 text-xs text-[#5C5C5C]"><Clock size={12} /> {v.type}</span>}
                        </div>
                      </div>
                      <Button variant="outline" className="rounded-none text-xs uppercase tracking-wider border-[#121212] h-9">
                        {selected === v.vacancy_id ? 'Close' : 'View Details'}
                      </Button>
                    </div>
                  </div>
                  {selected === v.vacancy_id && (
                    <div className="border-t border-[#E5E5E5] p-6">
                      {v.description && <p className="text-sm text-[#5C5C5C] mb-4 leading-relaxed">{v.description}</p>}
                      {v.responsibilities?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-bold uppercase tracking-wider mb-2">Roles & Responsibilities</p>
                          <ul className="space-y-1">{v.responsibilities.map((r, i) => (
                            <li key={i} className="text-sm text-[#5C5C5C] flex gap-2"><span className="text-[#121212]">-</span> {r}</li>
                          ))}</ul>
                        </div>
                      )}
                      {v.requirements?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-bold uppercase tracking-wider mb-2">Requirements</p>
                          <ul className="space-y-1">{v.requirements.map((r, i) => (
                            <li key={i} className="text-sm text-[#5C5C5C] flex gap-2"><span className="text-[#121212]">-</span> {r}</li>
                          ))}</ul>
                        </div>
                      )}
                      <Separator className="my-4" />
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-[#5C5C5C]" />
                        <span className="text-sm text-[#2A2A2A]">Apply at <strong>saidesigngroup2013@gmail.com</strong></span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
